import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from 'react-oidc-context';
import { listCampuses } from '../../api/campusPortalApi';
import {
  getCampusAiStats,
  listConversations as apiListConversations,
  getConversation as apiGetConversation,
  deleteConversation as apiDeleteConversation,
  askAiChat,
  AI_CHAT_MODELS
} from '../../api/aiChatApi';
import * as S from './Management.styles';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px;
`;

const SidePanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  max-height: 700px;
`;

const NewChatButton = styled.button`
  width: 100%;
  padding: 10px 14px;
  background: #6366F1;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 14px;

  &:hover { opacity: 0.9; }
`;

const PanelTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  margin: -4px;
  padding: 4px;
`;

const ConvItem = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  background: ${(p) => (p.$active ? '#EEF2FF' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? '#C7D2FE' : 'transparent')};
  position: relative;

  &:hover {
    background: ${(p) => (p.$active ? '#EEF2FF' : '#F3F4F6')};
  }
`;

const ConvTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 20px;
`;

const ConvMeta = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`;

const DeleteX = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;

  &:hover { background: #FEE2E2; color: #DC2626; }
`;

const EmptyList = styled.div`
  padding: 20px 8px;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  background: white;
`;

const ControlsRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const ControlBox = styled.div`
  flex: 1;
  min-width: 200px;
`;

const ConvHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #4B5563;

  strong { color: #111827; }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const StatPill = styled.div`
  background: #F3F4F6;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  color: #374151;

  strong { color: #111827; font-weight: 700; }
`;

const TestingBanner = styled.div`
  background: #FEF3C7;
  border: 1px solid #FDE68A;
  color: #92400E;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const Messages = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  min-height: 360px;
  max-height: 540px;
  overflow-y: auto;
  margin-bottom: 12px;
`;

const Bubble = styled.div`
  background: ${(p) => (p.role === 'user' ? '#EEF2FF' : 'white')};
  border: 1px solid ${(p) => (p.role === 'user' ? '#C7D2FE' : '#e5e7eb')};
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 10px;
  font-size: 14px;
  color: #1f2937;
  white-space: ${(p) => (p.role === 'user' ? 'pre-wrap' : 'normal')};
`;

const BubbleHead = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const MarkdownBody = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #1f2937;

  h1, h2, h3, h4, h5, h6 {
    margin: 12px 0 6px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
  }
  h1 { font-size: 18px; }
  h2 { font-size: 16px; }
  h3 { font-size: 15px; }
  h4, h5, h6 { font-size: 14px; }

  p { margin: 6px 0; }

  ul, ol {
    margin: 6px 0;
    padding-left: 22px;
  }
  li { margin: 2px 0; }
  li > ul, li > ol { margin: 2px 0; }

  strong { font-weight: 700; color: #111827; }
  em { font-style: italic; }

  code {
    background: #f3f4f6;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 12.5px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }
  pre {
    background: #f3f4f6;
    padding: 10px 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
  }
  pre code {
    background: transparent;
    padding: 0;
    font-size: 12.5px;
  }

  blockquote {
    border-left: 3px solid #d1d5db;
    margin: 6px 0;
    padding: 2px 10px;
    color: #4b5563;
  }

  a {
    color: #6366F1;
    text-decoration: underline;
  }

  table {
    border-collapse: collapse;
    margin: 8px 0;
    font-size: 13px;
    width: 100%;
  }
  th, td {
    border: 1px solid #e5e7eb;
    padding: 6px 10px;
    text-align: left;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
  }

  hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 10px 0;
  }

  & > *:first-child { margin-top: 0; }
  & > *:last-child { margin-bottom: 0; }
`;

const ChatInputRow = styled.div`
  display: flex;
  gap: 10px;
`;

const TextArea = styled.textarea`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
`;

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const AiChat = () => {
  const auth = useAuth();
  const userGroups = auth.user?.profile?.['cognito:groups'] || [];
  const isSystemAdmin = userGroups.includes('portal_system_admin');
  const isDeveloper = userGroups.includes('portal_developer');
  const hasAccess = isSystemAdmin || isDeveloper;

  const [campuses, setCampuses] = useState([]);
  const [loadingCampuses, setLoadingCampuses] = useState(true);

  // Conversation list
  const [conversations, setConversations] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(false);

  // Active conversation (null = "new chat" mode)
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);

  // New-chat controls
  const [campusId, setCampusId] = useState('');
  const [model, setModel] = useState(AI_CHAT_MODELS[0].value);
  const [newChatStats, setNewChatStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Chat state
  const [input, setInput] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState(null);
  const messagesRef = useRef(null);

  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, asking]);

  const refreshConversations = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const list = await apiListConversations();
      setConversations(list);
    } catch (err) {
      setError(`Failed to load conversations: ${err.message}`);
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  useEffect(() => {
    if (!hasAccess) {
      setLoadingCampuses(false);
      return;
    }
    listCampuses(true)
      .then((items) => {
        setCampuses(items);
        if (items.length > 0) setCampusId(items[0].id);
      })
      .catch((err) => setError(`Failed to load campuses: ${err.message}`))
      .finally(() => setLoadingCampuses(false));
    refreshConversations();
  }, [hasAccess, refreshConversations]);

  // Load stats when starting a new chat and campus changes
  useEffect(() => {
    if (activeConv || !campusId) {
      setNewChatStats(null);
      return;
    }
    setLoadingStats(true);
    setError(null);
    getCampusAiStats(campusId)
      .then((res) => setNewChatStats(res))
      .catch((err) => {
        setError(`Failed to load campus stats: ${err.message}`);
        setNewChatStats(null);
      })
      .finally(() => setLoadingStats(false));
  }, [campusId, activeConv]);

  const handleSelectConv = async (convId) => {
    if (asking) return;
    setError(null);
    try {
      const conv = await apiGetConversation(convId);
      setActiveConv(conv);
      setMessages(conv.messages || []);
      setModel(conv.model || AI_CHAT_MODELS[0].value);
    } catch (err) {
      setError(`Failed to open conversation: ${err.message}`);
    }
  };

  const handleNewChat = () => {
    if (asking) return;
    setActiveConv(null);
    setMessages([]);
    setInput('');
    setError(null);
  };

  const handleDelete = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await apiDeleteConversation(convId);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (activeConv && activeConv.id === convId) {
        setActiveConv(null);
        setMessages([]);
      }
    } catch (err) {
      setError(`Failed to delete: ${err.message}`);
    }
  };

  const handleAsk = async () => {
    const question = input.trim();
    if (!question || asking) return;
    if (!activeConv && !campusId) {
      setError('Select a campus to start a new chat.');
      return;
    }

    const optimisticUser = { role: 'user', text: question };
    setMessages((prev) => [...prev, optimisticUser]);
    setInput('');
    setAsking(true);
    setError(null);

    try {
      const res = await askAiChat({
        conversationId: activeConv?.id,
        campusId: activeConv ? undefined : campusId,
        question,
        model
      });
      const conv = res.conversation;
      setActiveConv({ ...conv, messages: undefined });
      setMessages(conv.messages || []);
      // Refresh the conversation list so ordering / new entry show up
      refreshConversations();
    } catch (err) {
      setError(err.message);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: `Error: ${err.message}`, isError: true }
      ]);
    } finally {
      setAsking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleAsk();
    }
  };

  if (auth.isLoading) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>AI Chat</S.Title>
            <S.Subtitle>Loading...</S.Subtitle>
          </div>
        </S.Header>
      </S.Container>
    );
  }

  if (!hasAccess) {
    return (
      <S.Container>
        <S.Header>
          <div>
            <S.Title>AI Chat</S.Title>
            <S.Subtitle>Access Restricted</S.Subtitle>
          </div>
        </S.Header>
        <S.ErrorBanner>
          This feature is restricted to System Admins.
        </S.ErrorBanner>
      </S.Container>
    );
  }

  const stats = activeConv?.stats || newChatStats?.stats;
  const activeCampusName = activeConv?.campusName
    || (campuses.find((c) => c.id === campusId)?.name ?? '');
  const disableSend = asking || !input.trim() || (!activeConv && !campusId);

  return (
    <S.Container>
      <S.Header>
        <div>
          <S.Title>AI Chat</S.Title>
          <S.Subtitle>
            Ask questions about course groups, schedules, students and teachers for a selected campus.
          </S.Subtitle>
        </div>
      </S.Header>

      <TestingBanner>
        Internal testing feature. Each new chat snapshots the selected campus data (schedules, classrooms, students, teachers — no billing) and sends it as Gemini&apos;s system context. Follow-up messages in the same chat reuse that snapshot. Conversations are saved per user.
      </TestingBanner>

      {error && <S.ErrorBanner>{error}</S.ErrorBanner>}

      <Grid>
        <SidePanel>
          <NewChatButton onClick={handleNewChat}>+ New Chat</NewChatButton>
          <PanelTitle>Your Conversations</PanelTitle>
          <ConversationList>
            {loadingConvs && <EmptyList>Loading...</EmptyList>}
            {!loadingConvs && conversations.length === 0 && (
              <EmptyList>No conversations yet. Start a new chat.</EmptyList>
            )}
            {conversations.map((c) => (
              <ConvItem
                key={c.id}
                $active={activeConv?.id === c.id}
                onClick={() => handleSelectConv(c.id)}
              >
                <ConvTitle>{c.title}</ConvTitle>
                <ConvMeta>
                  {c.campusName} · {c.messageCount} msgs · {formatRelative(c.updatedAt)}
                </ConvMeta>
                <DeleteX
                  onClick={(e) => handleDelete(c.id, e)}
                  title="Delete conversation"
                >
                  ×
                </DeleteX>
              </ConvItem>
            ))}
          </ConversationList>
        </SidePanel>

        <Panel>
          {activeConv ? (
            <ConvHeader>
              <div>
                <strong>{activeConv.title}</strong>
                <span style={{ marginLeft: 10 }}>
                  {activeConv.campusName} · {activeConv.messageCount || messages.length} messages
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#6B7280' }}>
                Context locked to {activeConv.campusName}
              </div>
            </ConvHeader>
          ) : (
            <ControlsRow>
              <ControlBox>
                <Label>Campus (for new chat)</Label>
                <Select
                  value={campusId}
                  onChange={(e) => setCampusId(e.target.value)}
                  disabled={loadingCampuses}
                >
                  {loadingCampuses && <option value="">Loading...</option>}
                  {!loadingCampuses && campuses.length === 0 && (
                    <option value="">No campuses available</option>
                  )}
                  {campuses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              </ControlBox>
              <ControlBox>
                <Label>Model</Label>
                <Select value={model} onChange={(e) => setModel(e.target.value)}>
                  {AI_CHAT_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Select>
              </ControlBox>
            </ControlsRow>
          )}

          {activeConv && (
            <Field>
              <Label>Model (used for next message)</Label>
              <Select value={model} onChange={(e) => setModel(e.target.value)}>
                {AI_CHAT_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Select>
            </Field>
          )}

          {(loadingStats || stats) && (
            <StatsRow>
              {loadingStats && <StatPill>Loading stats for {activeCampusName}...</StatPill>}
              {!loadingStats && stats && (
                <>
                  <StatPill>Course groups: <strong>{stats.courseGroupCount ?? 0}</strong></StatPill>
                  <StatPill>Students: <strong>{stats.studentCount ?? 0}</strong></StatPill>
                  <StatPill>Teachers: <strong>{stats.teacherCount ?? 0}</strong></StatPill>
                  <StatPill>Rooms: <strong>{stats.roomCount ?? 0}</strong></StatPill>
                </>
              )}
            </StatsRow>
          )}

          <Messages ref={messagesRef}>
            {messages.length === 0 && (
              <div style={{ color: '#6b7280', fontSize: 13 }}>
                {activeConv
                  ? 'No messages in this conversation yet.'
                  : 'Start a new chat by asking a question below. The selected campus data will be snapshotted as context.'}
              </div>
            )}
            {messages.map((m, idx) => (
              <Bubble key={idx} role={m.role}>
                <BubbleHead>
                  {m.role === 'user' ? 'You' : `AI (${m.model || model})`}
                  {m.usage && (
                    <span style={{ marginLeft: 8, fontWeight: 400, color: '#9ca3af' }}>
                      {m.usage.totalTokenCount} tokens
                    </span>
                  )}
                </BubbleHead>
                {m.role === 'assistant' && !m.isError ? (
                  <MarkdownBody>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
                  </MarkdownBody>
                ) : (
                  m.text
                )}
              </Bubble>
            ))}
            {asking && (
              <Bubble role="assistant">
                <BubbleHead>AI ({model})</BubbleHead>
                Thinking...
              </Bubble>
            )}
          </Messages>

          <ChatInputRow>
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                activeConv
                  ? 'Continue this conversation (Cmd/Ctrl+Enter to send)'
                  : 'Ask the first question to start a new chat (Cmd/Ctrl+Enter to send)'
              }
              disabled={asking}
            />
            <S.Button
              color="#6366F1"
              onClick={handleAsk}
              disabled={disableSend}
            >
              {asking ? 'Asking...' : 'Send'}
            </S.Button>
          </ChatInputRow>
        </Panel>
      </Grid>
    </S.Container>
  );
};

export default AiChat;
